import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Copy, Check, Eye, Code2, Sparkles, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useBranch } from '../context/BranchContext';

// External embed code must always contain a fully-qualified CredBloom URL.
const PUBLIC_EMBED_ORIGIN = (
  process.env.REACT_APP_PUBLIC_EMBED_URL || 'https://credbloom.itsmmp.com'
).replace(/\/$/, '');

function CodeBlock({ value, testId }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Complete embed code copied');
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error('Copy failed. Select the code manually.');
    }
  };

  return (
    <div className="relative group">
      <pre
        className="bg-slate-950 text-slate-100 text-xs font-mono p-4 pr-14 rounded-xl whitespace-pre-wrap break-words leading-relaxed border border-slate-800"
        data-testid={testId}
      >
        <code>{value}</code>
      </pre>
      <Button
        size="sm"
        variant="secondary"
        onClick={handleCopy}
        className="absolute top-2 right-2 h-7 gap-1.5 text-xs"
        data-testid={`${testId}-copy`}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

export default function EmbedReviewsTab() {
  const { currentBranch } = useBranch();

  const branchId = currentBranch?.id || 'YOUR_BRANCH_ID';
  const widgetUrl = `${PUBLIC_EMBED_ORIGIN}/api/embed/branch/${branchId}/widget?v=3`;

  // Universal embed deliberately uses only an iframe. This avoids CORS,
  // authentication, script execution and CMS JavaScript restrictions entirely.
  const htmlSnippet = useMemo(
    () =>
      `<!-- CredBloom Google Reviews Widget -->\n<iframe\n  src="${widgetUrl}"\n  title="Google Reviews"\n  width="100%"\n  height="330"\n  loading="lazy"\n  scrolling="no"\n  style="width:100%;height:330px;border:0;display:block;background:transparent;overflow:hidden;"\n></iframe>`,
    [widgetUrl]
  );

  const reactSnippet = useMemo(
    () =>
      `export function CredBloomReviews() {\n  return (\n    <iframe\n      src="${widgetUrl}"\n      title="Google Reviews"\n      width="100%"\n      height="330"\n      loading="lazy"\n      scrolling="no"\n      style={{\n        width: '100%',\n        height: 330,\n        border: 0,\n        display: 'block',\n        background: 'transparent',\n        overflow: 'hidden'\n      }}\n    />\n  );\n}`,
    [widgetUrl]
  );

  return (
    <div className="space-y-5">
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardContent className="pt-5 pb-5 flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <Code2 size={20} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
              Embed your Google reviews anywhere
              <span className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                Live
              </span>
            </h3>
            <p className="text-sm text-muted-foreground">
              Copy the iframe below and paste it into any external website, landing page,
              WordPress/Shopify custom HTML block, or CMS. No API request or CredBloom login
              runs inside the external website.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Code2 size={14} className="text-primary" />
              HTML / Universal
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Recommended. Paste directly into an HTML/custom-code block. No JavaScript or API key required.
            </p>
          </CardHeader>
          <CardContent>
            <CodeBlock value={htmlSnippet} testId="embed-html-snippet" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles size={14} className="text-primary" />
              React / Next.js
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Drop this component into any client-side React/Next.js page or layout.
            </p>
          </CardHeader>
          <CardContent>
            <CodeBlock value={reactSnippet} testId="embed-react-snippet" />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-sm flex items-center gap-2">
            <Eye size={14} className="text-primary" />
            Live preview
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-xs h-7"
            onClick={() => window.open(widgetUrl, '_blank', 'noopener,noreferrer')}
            data-testid="embed-open-preview-btn"
          >
            <ExternalLink size={12} /> Open in new tab
          </Button>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl border border-border overflow-hidden bg-muted/20">
            {currentBranch ? (
              <iframe
                title="CredBloom Reviews Preview"
                src={widgetUrl}
                className="w-full bg-transparent"
                style={{ height: 330, border: 0, display: 'block' }}
                scrolling="no"
                data-testid="embed-preview-iframe"
              />
            ) : (
              <div className="h-[330px] flex items-center justify-center text-sm text-muted-foreground">
                Pick a branch to preview its Google reviews.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">How it works</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="text-sm space-y-2 text-muted-foreground list-decimal pl-5">
            <li>Copy the complete HTML iframe snippet above.</li>
            <li>Paste it into an HTML/custom-code section on the external website.</li>
            <li>CredBloom renders the synced Google reviews inside its own secure iframe.</li>
            <li>The external website does not need CredBloom credentials, CORS configuration, or JavaScript permissions.</li>
            <li>The widget refreshes itself periodically so newly synced reviews appear automatically.</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
